/* eslint-disable @typescript-eslint/no-unused-vars */
'use client';

import { IconButton, Stack } from '@castlery/fortress';
import { useBreakpoints } from '@castlery/fortress/hooks';
import { ArrowLeft, ArrowRight } from '@castlery/fortress/Icons';
import { MappedSocialUgcItem } from '@castlery/modules-product-domain';
import { useCallback, useRef, useState } from 'react';
import Slider from 'react-slick';
import { SocialUgcDrawer } from './social-ugc-drawer';
import { SocialUgcImage } from './social-ugc-image';
import SocialUgcModal from './social-ugc-modal';
import { EVENT_SOCIAL_WIDGET } from '@castlery/modules-tracking-services';
import { useAppDispatch } from '@castlery/shared-redux-store';
import { useFirstInView } from '@castlery/modules-tracking-components';

interface SocialUgcCarouselProps {
  socialSortedUgc: MappedSocialUgcItem[];
}

export function SocialUgcCarousel(props: SocialUgcCarouselProps) {
  const { socialSortedUgc } = props;
  const { desktop, tablet } = useBreakpoints();
  const dispatch = useAppDispatch();
  const sliderRef = useRef<Slider>(null);
  const [open, setOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const SLIDE_TO_SHOW = desktop ? 4 : tablet ? 3 : 1;

  const sliderSettings = {
    dots: false,
    infinite: true,
    speed: 500,
    slidesToShow: SLIDE_TO_SHOW,
    slidesToScroll: SLIDE_TO_SHOW,
    arrows: false,
    initialSlide: 0,
    responsive: [
      {
        breakpoint: 899,
        settings: {
          slidesToShow: SLIDE_TO_SHOW,
          slidesToScroll: SLIDE_TO_SHOW,
        },
      },
      {
        breakpoint: 600,
        settings: {
          infinite: false,
          slidesToShow: SLIDE_TO_SHOW + 0.5,
          slidesToScroll: SLIDE_TO_SHOW,
        },
      },
    ],
  };

  const handleTrack = useCallback(
    async ({ action, label, position }: { action: string; label?: string | number; position?: number }) => {
      await dispatch(EVENT_SOCIAL_WIDGET({ action, label, position }));
    },
    [dispatch]
  );

  const socialUgcRef = useFirstInView(
    () => {
      handleTrack({ action: 'widget_impression' });
      trackImageImpressionsAfterSlide(0);
    },
    {
      threshold: 0.3,
    }
  );

  const goToPrev = () => {
    if (sliderRef.current) {
      sliderRef.current.slickPrev();
      handleTrack({ action: 'arrow_click' });
    }
  };

  const goToNext = () => {
    if (sliderRef.current) {
      sliderRef.current.slickNext();
      handleTrack({ action: 'arrow_click' });
    }
  };

  const trackImageImpressionsAfterSlide = useCallback(
    (currentIndex: number) => {
      const currentItems = socialSortedUgc?.filter(
        (_, index) => index >= currentIndex && index < currentIndex + SLIDE_TO_SHOW
      );
      if (Array.isArray(currentItems)) {
        currentItems.forEach((item, index) => {
          handleTrack({ action: 'image_impression', label: item._uid, position: currentIndex + index + 1 });
        });
      }
    },
    [handleTrack, socialSortedUgc, SLIDE_TO_SHOW]
  );

  return (
    <>
      <Stack
        sx={(theme) => ({
          width: '100%',
          '.slick-slider': {
            width: '100%',
            touchAction: 'pan-y',
            userSelect: 'none',
            overflowX: 'hidden',
            '& .slick-list': {
              overflowX: 'hidden',
              marginRight: -3,
              marginLeft: -1,
            },
            '& .slick-track': {
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 4,
            },
          },
        })}
        mt={desktop ? 7 : 5}
        ref={socialUgcRef as any}
      >
        <Slider ref={sliderRef} {...sliderSettings} afterChange={trackImageImpressionsAfterSlide}>
          {socialSortedUgc?.map((ugc, index) => (
            <SocialUgcImage
              ugc={ugc}
              {...{
                onClick: () => {
                  setCurrentIndex(index);
                  setOpen(true);
                  handleTrack({
                    action: ugc.fileType === 'video' ? 'video_click' : 'image_click',
                    label: ugc._uid,
                    position: index + 1,
                  });
                },
                key: `${ugc?._uid}-${index}`,
              }}
            />
          ))}
        </Slider>
        <Stack
          direction="row"
          justifyContent={'flex-end'}
          alignItems={'center'}
          mt={desktop ? 7 : 5}
          gap={desktop ? 6 : 4}
          mr={!desktop ? 6 : undefined}
        >
          <IconButton
            variant="image"
            aria-label="Previous UGC"
            title="Previous UGC"
            sx={{
              '--IconButton-size': '40px',
            }}
            onClick={goToPrev}
          >
            <ArrowLeft />
          </IconButton>
          <IconButton
            variant="image"
            aria-label="Next UGC"
            title="Next UGC"
            sx={{
              '--IconButton-size': '40px',
            }}
            onClick={goToNext}
          >
            <ArrowRight />
          </IconButton>
        </Stack>
      </Stack>
      {desktop ? (
        <SocialUgcModal
          open={open}
          onClose={() => setOpen(false)}
          ugcData={socialSortedUgc}
          initialIndex={currentIndex}
        />
      ) : (
        <SocialUgcDrawer
          open={open}
          onClose={() => setOpen(false)}
          ugcData={socialSortedUgc}
          initialIndex={currentIndex}
        />
      )}
    </>
  );
}
